/**
 * Task 2 — Open RMAs KPI tile for the Dashboard.
 *
 * Self-fetching. Owner/ops only (gating happens in the parent).
 *
 * Tile semantics:
 *   - Whole tile click → /rma-records
 *   - Secondary "awaiting vendor" click → /rma-records?status=submitted
 *     (stopPropagation so it doesn't double-fire the parent tile click)
 *
 * Error state: render "—" for both numbers, tile still navigable.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PackageIcon } from "@phosphor-icons/react";

import api from "../../lib/api";

export default function RmaKpiTile() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null); // null = loading, {} = loaded, 'err'
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/rma-records/stats");
        if (!cancelled) setStats(data);
      } catch (e) {
        // Match MrrTile convention — log, silently degrade.
        // eslint-disable-next-line no-console
        console.error("RmaKpiTile stats fetch failed", e);
        if (!cancelled) {
          setErr(true);
          setStats({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = stats === null;
  const openCount = !err && !loading ? stats.open_count ?? 0 : null;
  const awaiting = !err && !loading ? stats.awaiting_vendor_count ?? 0 : null;

  return (
    <div
      data-testid="kpi-rma-open"
      onClick={() => nav("/rma-records")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          nav("/rma-records");
        }
      }}
      className="bg-white border border-slate-200 rounded-md p-5 shadow-card hover:shadow-lift hover:border-slate-300 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Open RMAs
        </div>
        <PackageIcon size={16} weight="regular" className="text-slate-400" />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        {loading ? (
          <div
            data-testid="kpi-rma-open-loading"
            className="h-8 w-16 rounded bg-slate-100 animate-pulse"
          />
        ) : (
          <div
            data-testid="kpi-rma-open-value"
            className="font-heading text-4xl font-bold text-slate-900 tabular leading-none"
          >
            {err ? "—" : openCount}
          </div>
        )}
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          claims
        </div>
      </div>
      <div className="mt-2 text-[11px] text-slate-500">
        {loading ? (
          <div className="h-3 w-24 rounded bg-slate-100 animate-pulse" />
        ) : (
          <Link
            to="/rma-records?status=submitted"
            data-testid="kpi-rma-awaiting-link"
            onClick={(e) => e.stopPropagation()}
            className="hover:text-amber-700 hover:underline"
          >
            <span className="font-semibold text-slate-700">
              {err ? "—" : awaiting}
            </span>{" "}
            awaiting vendor
          </Link>
        )}
      </div>
    </div>
  );
}
